import React, { useState } from "react";
import { api, errMsg, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, TrendingUp, Sparkles } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function Forecast() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/ai/forecast"); setData(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><TrendingUp className="h-6 w-6 text-[#F47920]" /> AI Demand Forecasting</h1>
          <p className="text-sm text-slate-500 mt-0.5">Claude-powered demand & inventory projection grounded on historical lifting data.</p>
        </div>
        <Button className="bg-[#12346B] hover:bg-[#12346B]/90" onClick={run} disabled={busy} data-testid="btn-run-forecast">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Generate Forecast
        </Button>
      </div>

      {!data ? (
        <div className="bg-white border border-slate-200 rounded-lg py-16 text-center text-slate-400 text-sm">
          Click “Generate Forecast” to project next-period petroleum demand and inventory adequacy.
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Avg Monthly Demand</div>
              <div className="font-head text-2xl font-bold text-slate-900 mt-1">{fmtNum(data.avg_monthly_demand)} gal</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Next-Month Projection</div>
              <div className="font-head text-2xl font-bold text-[#F47920] mt-1">{fmtNum(data.next_month_projection)} gal</div>
            </div>
          </div>

          {data.monthly?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm font-semibold text-slate-700 mb-3">Historical Monthly Flow (Gallons)</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend />
                  <Bar dataKey="receipts" name="Receipts" fill="#12346B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="liftings" name="Liftings" fill="#F47920" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="forecast-narrative">
            <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-[#F47920]" /><span className="font-head font-semibold text-slate-900">AI Analyst Narrative</span></div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{data.narrative}</div>
          </div>
        </div>
      )}
    </div>
  );
}
